import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { SchedDiscConfigComponentBase } from "../SchedDiscConfigComponentBase";
import { TimeSpan } from "DiscoveryEngine.TS/Utils";
import { NetFacadeService } from 'netview';
import { Device } from '../../../DiscoveryEngine.TS/Core';

@Component({
    selector: 'sched-disc-config-advanced',
	templateUrl: './sched-disc-config-advanced.component.html',
	styleUrls: ['./sched-disc-config-advanced.component.scss']
})
export class SchedDiscConfigAdvancedComponent extends SchedDiscConfigComponentBase {

	public PingFirst: UntypedFormControl;
	public PingTimeout: UntypedFormControl;
	public PingRetries: UntypedFormControl;
	public ResolveHostnames: UntypedFormControl;
	public ResolveNetBIOS: UntypedFormControl;
	public DeviceMergeSetting: UntypedFormControl;
	public MaxThreads: UntypedFormControl;
	public MaxDeviceTime: UntypedFormControl;
	public SnmpReadDelay: UntypedFormControl;
	public CaptureConfigs: UntypedFormControl;
	public CaptureLogs: UntypedFormControl;
	public ConfigExportFolder: UntypedFormControl;
	public CreateDateSubfolder: UntypedFormControl;
	public CreateDeviceSubfolder: UntypedFormControl;
	public SaveChangesOnly: UntypedFormControl;
	public ExportToServer: UntypedFormControl;
	public AllowIPv6Crawl: UntypedFormControl;
	public TcpPorts: UntypedFormControl;
	public UdpPorts: UntypedFormControl;
	public KeepOpenPortsOnly: UntypedFormControl;

	constructor(private fb: UntypedFormBuilder, netFacadeService: NetFacadeService) {
		super(netFacadeService);
		this.BuildForm();
	}

	protected BuildForm(): void {
		let pingFirst = false;
		let pingTimeout = 0;
		let pingRetries = 0;
		let resolveHostnames = false;
		let resolveNetBIOS = true;
		let deviceMergeSetting = "";
		let maxThreads = 0;
		let maxDeviceTime = 0;
		let snmpReadDelay = 0;
		let allowIPv6Crawl = false;
		let captureConfigs = false;
		let captureLogs = false;
		let configExportFolder = "";
		let createDateSubfolder = false;
		let createDeviceSubfolder = false;
		let saveChangesOnly = false;
		let exportToServer = false;
		let tcpPorts = "";
		let udpPorts = "";
		let keepOpenPortsOnly = false;

		if (this.Settings) {
			pingFirst = this.Settings.PingFirst;
			pingTimeout = this.Settings.PingTimeout;
			pingRetries = this.Settings.PingRetries;
			resolveHostnames = this.Settings.ResolveHostnames;
			resolveNetBIOS = this.Settings.ResolveNetBIOS;
			deviceMergeSetting = this.Discovery.DeviceMergeSetting;
			maxThreads = this.Settings.MaxThreads;
			maxDeviceTime = this.Settings.MaxDeviceTime.Minutes;
			snmpReadDelay = this.Settings.SnmpReadDelay;
			allowIPv6Crawl = this.Settings.AllowIPv6Crawl;
			captureConfigs = this.Settings.CaptureConfigs;
			captureLogs = this.Settings.CaptureLogs;
			configExportFolder = (this.Discovery.HasConfigExportFolder ? this.Discovery.ConfigExportFolder : "");
			createDateSubfolder = this.Discovery.CreateDateSubfolder;
			createDeviceSubfolder = this.Discovery.CreateDeviceSubfolder;
			saveChangesOnly = this.Discovery.ConfigSaveChangesOnly;
			exportToServer = this.Discovery.ExportConfigsToServer;
			tcpPorts = this.Settings.TcpPorts;
			udpPorts = this.Settings.UdpPorts;
			keepOpenPortsOnly = this.Settings.KeepOpenPortsOnly;
		}

		this.PingFirst = new UntypedFormControl(pingFirst);
		this.PingTimeout = new UntypedFormControl(pingTimeout, Validators.compose([this.minValue(250), this.maxValue(25000)]));
		this.PingRetries = new UntypedFormControl(pingRetries, Validators.compose([this.minValue(0), this.maxValue(10)]));
		this.ResolveHostnames = new UntypedFormControl(resolveHostnames);
		this.ResolveNetBIOS = new UntypedFormControl(resolveNetBIOS);
		this.DeviceMergeSetting = new UntypedFormControl(deviceMergeSetting);
		this.MaxThreads = new UntypedFormControl(maxThreads, Validators.compose([this.minValue(1), this.maxValue(250)]));
		this.MaxDeviceTime = new UntypedFormControl(maxDeviceTime, Validators.compose([this.minValue(0), this.maxValue(59)]));
		this.SnmpReadDelay = new UntypedFormControl(snmpReadDelay, Validators.compose([this.minValue(0), this.maxValue(15000)]));
		this.AllowIPv6Crawl = new UntypedFormControl(allowIPv6Crawl);
		this.CaptureConfigs = new UntypedFormControl(captureConfigs);
		this.CaptureLogs = new UntypedFormControl(captureLogs);
		this.ConfigExportFolder = new UntypedFormControl(configExportFolder);
		this.CreateDateSubfolder = new UntypedFormControl(createDateSubfolder);
		this.CreateDeviceSubfolder = new UntypedFormControl(createDeviceSubfolder);
		this.SaveChangesOnly = new UntypedFormControl(saveChangesOnly);
		this.ExportToServer = new UntypedFormControl(exportToServer);

		this.TcpPorts = new UntypedFormControl(tcpPorts);
		this.UdpPorts = new UntypedFormControl(udpPorts);
		this.KeepOpenPortsOnly = new UntypedFormControl(keepOpenPortsOnly);

		this.enableDisableControls();

		this.SettingsForm = this.fb.group({
			PingFirst: this.PingFirst,
			PingTimeout: this.PingTimeout,
			PingRetries: this.PingRetries,
			ResolveHostnames: this.ResolveHostnames,
			ResolveNetBIOS: this.ResolveNetBIOS,
			DeviceMergeSetting: this.DeviceMergeSetting,
			MaxThreads: this.MaxThreads,
			MaxDeviceTime: this.MaxDeviceTime,
			SnmpReadDelay: this.SnmpReadDelay,
			AllowIPv6Crawl: this.AllowIPv6Crawl,
			CaptureConfigs: this.CaptureConfigs,
			CaptureLogs: this.CaptureLogs,
			ConfigExportFolder: this.ConfigExportFolder,
			CreateDateSubfolder: this.CreateDateSubfolder,
			CreateDeviceSubfolder: this.CreateDeviceSubfolder,
			SaveChangesOnly: this.SaveChangesOnly,
			ExportToServer: this.ExportToServer,
			TcpPorts: this.TcpPorts,
			UdpPorts: this.UdpPorts,
			KeepOpenPortsOnly: this.KeepOpenPortsOnly,
		});
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.CaptureLogs, this.CaptureConfigs.value);
		this.enableDisableFormControl(this.ConfigExportFolder, this.CaptureConfigs.value);
		this.enableDisableFormControl(this.CreateDateSubfolder, this.CaptureConfigs.value);
		this.enableDisableFormControl(this.CreateDeviceSubfolder, this.CaptureConfigs.value);
		this.enableDisableFormControl(this.SaveChangesOnly, this.CaptureConfigs.value);
		this.enableDisableFormControl(this.ExportToServer, this.CaptureConfigs.value);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.onChanges();
		this.BuildForm();
	}

	public applyTempChanges(): void {
		if (this.Settings) {
			this.Settings.PingFirst = this.PingFirst.value;
			this.Settings.PingTimeout = parseInt(this.PingTimeout.value);
			this.Settings.PingRetries = parseInt(this.PingRetries.value);
			this.Settings.ResolveHostnames = this.ResolveHostnames.value;
			this.Settings.ResolveNetBIOS = this.ResolveNetBIOS.value;
			this.Discovery.DeviceMergeSetting = this.DeviceMergeSetting.value;
			this.Settings.CaptureConfigs = this.CaptureConfigs.value;
			this.Settings.CaptureLogs = this.CaptureLogs.value;
			this.Settings.MaxThreads = parseInt(this.MaxThreads.value);
			this.Settings.MaxDeviceTime =
				TimeSpan.FromMinutes(parseInt(this.MaxDeviceTime.value));
			this.Settings.SnmpReadDelay = parseInt(this.SnmpReadDelay.value);
			this.Settings.AllowIPv6Crawl = this.AllowIPv6Crawl.value;
			this.Discovery.ConfigExportFolder = this.ConfigExportFolder.value;
			this.Discovery.CreateDateSubfolder = this.CreateDateSubfolder.value;
			this.Discovery.CreateDeviceSubfolder = this.CreateDeviceSubfolder.value;
			this.Discovery.ConfigSaveChangesOnly = this.SaveChangesOnly.value;
			this.Discovery.ExportConfigsToServer = this.ExportToServer.value;
			this.Settings.TcpPorts = this.TcpPorts.value;
			this.Settings.UdpPorts = this.UdpPorts.value;
			this.Settings.KeepOpenPortsOnly = this.KeepOpenPortsOnly.value;

			this.BuildForm();
			this.onApplyTempChanges();
		}
	}

	public ExcludeCategoryChanged(event: any, category: string): void {
		this.SettingsForm.markAsDirty();
	}

	public get invalidTempChanges(): string[] {
		return ["Advanced Settings"];
	}

}
